import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { debounce } from '@icp/utils';
import { AgTable } from '@icp/components';
import ACLDataSource from './ACLDataSource';
import { getRowsFromCachedData, setSelectedIfNeed } from './utils';

const ACLTableClientSide = forwardRef(function ACLTableClientSide(props, ref) {
  const {
    aclDataSource,
    position,
    multiple,
    mapping,
    selectedKeys = [],
    stringEqual,
    onSelectionChanged,
    onLoadTotalCount,
    ...other
  } = props;

  // If isLazy is false, ag-grid is clientSide, request all data and then apply to ag-grid
  const [rowData, setRowData] = useState(null);
  const gridRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getSelectedKeys: () => {
      const rows = gridRef.current.api.getSelectedRows();
      return rows.map((row) => (stringEqual ? String(row[mapping.value]) : row[mapping.value]));
    },
    deselectAll: () => {
      gridRef.current.api.deselectAll();
    },
  }));
  console.log('renderer');
  const calculateRowData = useCallback(
    (allRowsData) => {
      if (!position) {
        // is single select, not in transfer
        setRowData(allRowsData);
      }
      console.log('calculateRowData', allRowsData);
      if (position === 'left') {
        const data = allRowsData.filter((item) => {
          const key = stringEqual ? String(item[mapping.value]) : item[mapping.value];
          return !selectedKeys.includes(key);
        });
        setRowData(data);
      }
      if (position === 'right') {
        const data = getRowsFromCachedData(aclDataSource.cachedData, selectedKeys);
        setRowData(data);
      }
    },
    [aclDataSource, mapping.value, position, selectedKeys, stringEqual],
  );

  useEffect(() => {
    // selectedKeys change means init or transfer happens
    aclDataSource
      .fetchAllRows()
      .then((res) => {
        if (onLoadTotalCount) {
          onLoadTotalCount(res.length);
        }
        return res;
      })
      .then(calculateRowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys]);

  const handleFirstDataRendered = useCallback((params) => {
    setSelectedIfNeed(multiple, selectedKeys, params.api);

    if (multiple) {
      setTimeout(() => {
        const colIds = gridRef.current.api.getColumns().map((column) => column.colId);
        gridRef.current.api.autoSizeColumns(colIds);
      }, 16);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectionChanged = debounce((params) => {
    const rows = params.api.getSelectedRows();

    const hasSelect = rows.length > 0;

    if (onSelectionChanged) {
      onSelectionChanged(hasSelect);
    }
  }, 50);

  return (
    <AgTable
      {...other}
      rowModelType="clientSide"
      rowData={rowData}
      getRowId={(params) => String(params.data[mapping.value] ?? params.data.id)}
      rowSelection={{
        mode: multiple ? 'multiRow' : 'singleRow',
        isRowSelectable: (params) => !!params.data?.[mapping.value],
      }}
      onSelectionChanged={handleSelectionChanged}
      onFirstDataRendered={handleFirstDataRendered}
      ref={gridRef}
    />
  );
});

ACLTableClientSide.propTypes = {
  aclDataSource: PropTypes.instanceOf(ACLDataSource),
  position: PropTypes.oneOf(['left', 'right']),
  multiple: PropTypes.bool,
  mapping: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  stringEqual: PropTypes.bool,
  onSelectionChanged: PropTypes.func,
  onLoadTotalCount: PropTypes.func,
  rowModelType: PropTypes.string,
  pagination: PropTypes.bool,
};

export default ACLTableClientSide;
